import { clamp } from '../utils/clamp';

type SchedulerEntry = {
  timeoutId: ReturnType<typeof setTimeout>;
  scheduledAt: number;
  executeAt: number;
};

export const createScheduler = ({
  minPeriod,
  maxPeriod,
}: {
  minPeriod?: number;
  maxPeriod?: number;
} = {}) => {
  const schedulerQueue = new Map<string, SchedulerEntry>();

  const clear = (instanceKey: string) => {
    const entry = schedulerQueue.get(instanceKey);
    if (entry) {
      clearTimeout(entry.timeoutId);
      schedulerQueue.delete(instanceKey);

      return true;
    }

    return false;
  };

  return {
    clear,
    queue: (id: string, period: number, callback: (id: string) => void) => {
      clear(id);

      const clampedPeriod = clamp(
        period,
        minPeriod ?? period,
        maxPeriod ?? period
      );
      const now = Date.now();

      schedulerQueue.set(id, {
        timeoutId: setTimeout(() => {
          schedulerQueue.delete(id);
          callback(id);
        }, clampedPeriod),
        scheduledAt: now,
        executeAt: now + clampedPeriod,
      });
    },
    getStatus: (
      id: string
    ): { scheduledAt: number; executeAt: number } | null => {
      const entry = schedulerQueue.get(id);
      if (!entry) {
        return null;
      }
      return { scheduledAt: entry.scheduledAt, executeAt: entry.executeAt };
    },
    getAllStatuses: (): Record<
      string,
      { scheduledAt: number; executeAt: number }
    > => {
      const result: Record<string, { scheduledAt: number; executeAt: number }> =
        {};
      for (const [id, entry] of schedulerQueue) {
        result[id] = {
          scheduledAt: entry.scheduledAt,
          executeAt: entry.executeAt,
        };
      }
      return result;
    },
  };
};

export type Scheduler = ReturnType<typeof createScheduler>;
